import type { RenphoMeasurement, RenphoResponse } from '@circa/shared/protocol';
import { api } from '@/core/api';
import { batch } from '@/core/repo';
import { getSetting, setSetting } from '@/core/settings';
import { dayInZone } from '@/core/time';
import type { Measurement } from '@/core/types';
import { zoneAt } from '@/modules/sleep/where';
import { upsert } from './upsert';

// Renpho scale: weight and body composition. Renpho has no public API, so the server signs in
// to Renpho's app cloud with your Renpho email and password (kept in this account, encrypted)
// each time and passes the measurements back.

export const RENPHO_KEY = 'conn.renpho';

export interface RenphoLink {
  email: string;
  password: string;
  connectedAt: number;
  lastSync?: number;
  /** newest measurement seen (epoch ms) */
  lastAt?: number;
  error?: string;
}

export const getRenpho = () => getSetting<RenphoLink | null>(RENPHO_KEY, null);

export function measurementFields(
  m: RenphoMeasurement,
): Omit<Measurement, 'id' | 'kind' | 'createdAt' | 'updatedAt'> {
  const tz = zoneAt(m.at);
  return {
    at: m.at,
    day: dayInZone(m.at, tz),
    tz,
    weightKg: m.weightKg,
    bodyFatPct: m.bodyFatPct,
    muscleKg: m.muscleKg,
    musclePct: m.musclePct,
    skeletalMusclePct: m.skeletalMusclePct,
    waterPct: m.waterPct,
    boneKg: m.boneKg,
    proteinPct: m.proteinPct,
    visceralFat: m.visceralFat,
    subcutaneousFatPct: m.subcutaneousFatPct,
    bmr: m.bmr,
    metabolicAge: m.metabolicAge,
    leanMassKg: m.leanMassKg,
    bmi: m.bmi,
    heartRate: m.heartRate,
    source: 'renpho',
  };
}

export function applyRenpho(list: RenphoMeasurement[]): number {
  let n = 0;
  batch(() => {
    // One id per weigh-in moment: the same reading from an import and from a sync merge.
    for (const m of list)
      if (upsert('measurement', `renpho-${m.at}`, measurementFields(m), ['note']) !== 'same') n++;
  });
  return n;
}

/** Checks the email and password by syncing once; saves them only if Renpho accepts them. */
export async function connectRenpho(email: string, password: string): Promise<number> {
  const res = await api<RenphoResponse>('/api/renpho/measurements', {
    method: 'POST',
    body: { email: email.trim(), password },
  });
  const n = applyRenpho(res.measurements);
  setSetting(RENPHO_KEY, {
    email: email.trim(),
    password,
    connectedAt: Date.now(),
    lastSync: Date.now(),
    lastAt: res.measurements.at(-1)?.at,
  } satisfies RenphoLink);
  return n;
}

export function disconnectRenpho() {
  setSetting(RENPHO_KEY, null);
}

export async function syncRenpho(): Promise<number> {
  const link = getRenpho();
  if (!link) return 0;
  try {
    const res = await api<RenphoResponse>('/api/renpho/measurements', {
      method: 'POST',
      // A week of overlap: a reading can reach Renpho's cloud late.
      body: {
        email: link.email,
        password: link.password,
        since: (link.lastAt ?? 0) - 7 * 86_400_000,
      },
    });
    const n = applyRenpho(res.measurements);
    setSetting(RENPHO_KEY, {
      ...link,
      lastSync: Date.now(),
      lastAt: Math.max(link.lastAt ?? 0, res.measurements.at(-1)?.at ?? 0) || undefined,
      error: undefined,
    });
    return n;
  } catch (err) {
    setSetting(RENPHO_KEY, { ...link, error: (err as Error).message });
    throw err;
  }
}
